// Leaderboard Page — MVP Awards with auto-calculate, edit & delete
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import { StatusBadge, ScoreBar } from '../components/StatusBadge';
import { awardsApi, usersApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Trophy, Medal, Crown, Star, Zap, Award, Plus, Calculator, Edit, Trash2
} from 'lucide-react';

export default function LeaderboardPage() {
  const { user, isAdmin } = useAuth();
  const { t, language } = useLanguage();

  const [leaderboard, setLeaderboard] = useState([]);
  const [seniorLeaderboard, setSeniorLeaderboard] = useState([]);
  const [awards, setAwards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('quarter');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAward, setEditingAward] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [calcDialogOpen, setCalcDialogOpen] = useState(false);
  const [calcYear, setCalcYear] = useState(new Date().getFullYear());
  const [calcQuarter, setCalcQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));

  const [awardForm, setAwardForm] = useState({
    user_id: '',
    award_type: 'mvp_facilitator',
    period: 'quarter',
    year: new Date().getFullYear(),
    quarter: Math.ceil((new Date().getMonth() + 1) / 3)
  });

  useEffect(() => { loadData(); }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [leaderRes, awardsRes, usersRes] = await Promise.all([
        awardsApi.getLeaderboard(period),
        awardsApi.getAll({ year: new Date().getFullYear() }),
        usersApi.getAll()
      ]);
      setLeaderboard(leaderRes.data.facilitators || []);
      setSeniorLeaderboard(leaderRes.data.seniors || []);
      setAwards(awardsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAward = async () => {
    try {
      if (editingAward) {
        await awardsApi.update(editingAward.award_id, awardForm);
        toast.success(language === 'el' ? 'Βραβείο ενημερώθηκε!' : 'Award updated!');
      } else {
        await awardsApi.create(awardForm);
        toast.success(language === 'el' ? 'Βραβείο δημιουργήθηκε!' : 'Award created!');
      }
      setDialogOpen(false);
      setEditingAward(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleEditAward = (award) => {
    setEditingAward(award);
    setAwardForm({
      user_id: award.user_id,
      award_type: award.award_type,
      period: award.period || 'quarter',
      year: award.year,
      quarter: award.quarter
    });
    setDialogOpen(true);
  };

  const handleDeleteAward = async (awardId) => {
    if (!window.confirm(language === 'el' ? 'Διαγραφή βραβείου;' : 'Delete award?')) return;
    try {
      await awardsApi.delete(awardId);
      toast.success(language === 'el' ? 'Βραβείο διαγράφηκε!' : 'Award deleted!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleAutoCalculate = async () => {
    setCalculating(true);
    try {
      const res = await awardsApi.autoCalculate(calcYear, calcQuarter);
      const data = res.data;
      toast.success(data.message || (language === 'el' ? 'Βραβεία υπολογίστηκαν!' : 'Awards calculated!'));
      setCalcDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setCalculating(false);
    }
  };

  const openNewAwardDialog = () => {
    setEditingAward(null);
    setAwardForm({
      user_id: '',
      award_type: 'mvp_facilitator',
      period: 'quarter',
      year: new Date().getFullYear(),
      quarter: Math.ceil((new Date().getMonth() + 1) / 3)
    });
    setDialogOpen(true);
  };

  const timePeriods = [
    { value: 'this_month', label: t('thisMonth') },
    { value: 'quarter', label: t('quarter') },
    { value: 'semester', label: t('semester') },
    { value: 'year', label: t('year') },
    { value: 'all_time', label: t('allTime') },
  ];

  const awardTypes = [
    { value: 'mvp_facilitator', label: t('mvpFacilitator'), icon: Trophy, color: 'text-yellow-500' },
    { value: 'best_energy', label: t('bestEnergy'), icon: Zap, color: 'text-orange-500' },
    { value: 'best_reliability', label: t('bestReliability'), icon: Award, color: 'text-green-500' },
    { value: 'best_debriefer', label: t('bestDebriefer'), icon: Star, color: 'text-blue-500' },
  ];

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="text-yellow-500" size={24} />;
    if (rank === 2) return <Medal className="text-gray-400" size={24} />;
    if (rank === 3) return <Medal className="text-orange-400" size={24} />;
    return <span className="text-lg font-bold text-gray-400">{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
    if (rank === 2) return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
    if (rank === 3) return 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200';
    return 'bg-white border-gray-100';
  };

  const getUserName = (userId) => users.find(u => u.user_id === userId)?.name || 'Unknown';

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in-up" data-testid="leaderboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#2A1514]">{t('leaderboard')}</h1>
            <p className="text-gray-500 mt-1">
              {language === 'el' ? 'Κατάταξη και βραβεία MVP' : 'Rankings and MVP awards'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[160px]" data-testid="period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {timePeriods.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setCalcDialogOpen(true)}
                  data-testid="auto-calculate-btn"
                >
                  <Calculator size={16} className="mr-2" />
                  {language === 'el' ? 'Αυτόματος Υπολογισμός' : 'Auto-Calculate'}
                </Button>
                <Button className="bg-[#E6007E] hover:bg-[#b8005f]" onClick={openNewAwardDialog} data-testid="new-award-btn">
                  <Plus size={16} className="mr-2" />
                  {language === 'el' ? 'Χειροκίνητο Βραβείο' : 'Manual Award'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Awards Section */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-[#E6007E]/5 to-pink-50">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Trophy className="text-yellow-500" />
              {t('mvpAwards')} {new Date().getFullYear()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {awardTypes.map(awardType => {
                const award = awards.find(a => a.award_type === awardType.value);
                const Icon = awardType.icon;
                return (
                  <div key={awardType.value} className={`p-4 rounded-xl border relative group ${award ? 'bg-white' : 'bg-gray-50 border-dashed'}`}>
                    {isAdmin && award && (
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleEditAward(award)} data-testid={`edit-award-${awardType.value}`}>
                          <Edit size={12} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleDeleteAward(award.award_id)} data-testid={`delete-award-${awardType.value}`}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <Icon className={awardType.color} size={24} />
                      <span className="font-medium text-sm">{awardType.label}</span>
                    </div>
                    {award ? (
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-[#E6007E] text-white">
                            {getUserName(award.user_id).charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-[#2A1514]">{getUserName(award.user_id)}</p>
                          <p className="text-xs text-gray-500">Q{award.quarter} {award.year}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">
                        {language === 'el' ? 'Δεν έχει ανατεθεί' : 'Not assigned'}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Top Performers - Facilitators */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading">
              {language === 'el' ? 'Top Performers - Facilitators' : 'Top Performers - Facilitators'}
            </CardTitle>
            <CardDescription>
              {language === 'el' ? 'Κατάταξη βάσει μέσου όρου αξιολογήσεων' : 'Ranked by average evaluation score'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : leaderboard.length > 0 ? (
              <div className="space-y-3 stagger-children">
                {leaderboard.map((item, idx) => {
                  const rank = idx + 1;
                  return (
                    <div
                      key={item.user_id}
                      className={`flex items-center gap-4 p-4 rounded-xl border transition-all hover:shadow-md ${getRankBg(rank)} ${item.user_id === user?.user_id ? 'ring-2 ring-[#E6007E]/50' : ''}`}
                      data-testid={`facilitator-leaderboard-item-${rank}`}
                    >
                      <div className="w-10 h-10 flex items-center justify-center">{getRankIcon(rank)}</div>
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={item.picture} />
                        <AvatarFallback className="bg-[#E6007E] text-white">{item.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-[#2A1514] truncate">{item.name}</p>
                          {item.user_id === user?.user_id && (
                            <Badge variant="outline" className="text-xs">{language === 'el' ? 'Εσύ' : 'You'}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-sm text-gray-500">{item.evaluation_count} {language === 'el' ? 'αξιολογήσεις' : 'evaluations'}</span>
                          <StatusBadge status={item.status} size="small" showLabel={false} />
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${item.average_score >= 4 ? 'text-green-600' : item.average_score >= 3 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {item.average_score.toFixed(1)}
                        </p>
                        <p className="text-xs text-gray-400">/5</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Trophy size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">{t('noData')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Performers - Senior Facilitators */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading">
              {language === 'el' ? 'Top Performers - Senior Facilitators' : 'Top Performers - Senior Facilitators'}
            </CardTitle>
            <CardDescription>
              {language === 'el' ? 'Κατάταξη Senior Facilitators βάσει μέσου όρου αξιολογήσεων' : 'Senior Facilitators ranked by average evaluation score'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => (
                  <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : seniorLeaderboard.length > 0 ? (
              <div className="space-y-3 stagger-children">
                {seniorLeaderboard.map((item, idx) => {
                  const rank = idx + 1;
                  return (
                    <div
                      key={item.user_id}
                      className={`flex items-center gap-4 p-4 rounded-xl border transition-all hover:shadow-md ${getRankBg(rank)} ${item.user_id === user?.user_id ? 'ring-2 ring-[#E6007E]/50' : ''}`}
                      data-testid={`senior-leaderboard-item-${rank}`}
                    >
                      <div className="w-10 h-10 flex items-center justify-center">{getRankIcon(rank)}</div>
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={item.picture} />
                        <AvatarFallback className="bg-[#E6007E] text-white">{item.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-[#2A1514] truncate">{item.name}</p>
                          {item.user_id === user?.user_id && (
                            <Badge variant="outline" className="text-xs">{language === 'el' ? 'Εσύ' : 'You'}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-sm text-gray-500">{item.evaluation_count} {language === 'el' ? 'αξιολογήσεις' : 'evaluations'}</span>
                          <StatusBadge status={item.status} size="small" showLabel={false} />
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${item.average_score >= 4 ? 'text-green-600' : item.average_score >= 3 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {item.average_score.toFixed(1)}
                        </p>
                        <p className="text-xs text-gray-400">/5</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Medal size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">
                  {language === 'el' ? 'Δεν υπάρχουν δεδομένα Senior Facilitators' : 'No Senior Facilitator data'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Award Dialog (Create / Edit) */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingAward(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingAward
                  ? (language === 'el' ? 'Επεξεργασία Βραβείου' : 'Edit Award')
                  : (language === 'el' ? 'Νέο Βραβείο' : 'New Award')
                }
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('selectFacilitator')}</Label>
                <Select value={awardForm.user_id} onValueChange={(v) => setAwardForm({...awardForm, user_id: v})}>
                  <SelectTrigger data-testid="award-user-select"><SelectValue placeholder={language === 'el' ? 'Επιλέξτε...' : 'Select...'} /></SelectTrigger>
                  <SelectContent>
                    {users.filter(u => u.role !== 'admin').map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'el' ? 'Τύπος Βραβείου' : 'Award Type'}</Label>
                <Select value={awardForm.award_type} onValueChange={(v) => setAwardForm({...awardForm, award_type: v})}>
                  <SelectTrigger data-testid="award-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {awardTypes.map(a => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'el' ? 'Έτος' : 'Year'}</Label>
                  <Select value={awardForm.year.toString()} onValueChange={(v) => setAwardForm({...awardForm, year: parseInt(v)})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'el' ? 'Τρίμηνο' : 'Quarter'}</Label>
                  <Select value={awardForm.quarter?.toString()} onValueChange={(v) => setAwardForm({...awardForm, quarter: parseInt(v)})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4].map(q => (
                        <SelectItem key={q} value={q.toString()}>Q{q}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
              <Button onClick={handleSaveAward} className="bg-[#E6007E] hover:bg-[#b8005f]" disabled={!awardForm.user_id} data-testid="save-award-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Auto-Calculate Dialog */}
        <Dialog open={calcDialogOpen} onOpenChange={setCalcDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {language === 'el' ? 'Αυτόματος Υπολογισμός Βραβείων' : 'Auto-Calculate Awards'}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500">
              {language === 'el'
                ? 'Επιλέξτε έτος και τρίμηνο για τον υπολογισμό βραβείων βάσει αξιολογήσεων.'
                : 'Select year and quarter to calculate awards based on evaluations.'}
            </p>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div className="space-y-2">
                <Label>{language === 'el' ? 'Έτος' : 'Year'}</Label>
                <Select value={calcYear.toString()} onValueChange={(v) => setCalcYear(parseInt(v))}>
                  <SelectTrigger data-testid="calc-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{language === 'el' ? 'Τρίμηνο' : 'Quarter'}</Label>
                <Select value={calcQuarter.toString()} onValueChange={(v) => setCalcQuarter(parseInt(v))}>
                  <SelectTrigger data-testid="calc-quarter-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4].map(q => (
                      <SelectItem key={q} value={q.toString()}>Q{q}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCalcDialogOpen(false)}>{t('cancel')}</Button>
              <Button
                onClick={handleAutoCalculate}
                className="bg-[#E6007E] hover:bg-[#b8005f]"
                disabled={calculating}
                data-testid="run-auto-calculate-btn"
              >
                {calculating
                  ? (language === 'el' ? 'Υπολογισμός...' : 'Calculating...')
                  : (language === 'el' ? 'Υπολογισμός' : 'Calculate')
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
